/* eslint-disable jsx-a11y/no-autofocus */
import React, { FC, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import { IconButton, ThemeProvider, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GLOBALMESSAGES } from "../../constants/messages";
import appTheme from "../../assets/theme/theme";

interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  message: string;
  onClose: (confirmed: boolean) => void;
  onDiscard?: () => void;
  /**
   * Which footer button takes the initial focus. Defaults to "yes" so every existing caller
   * keeps its current behaviour; pass "no" when confirming is the destructive choice and
   * Enter-on-open must not perform it (Bug 82804 — PostIF discard changes).
   */
  initialFocus?: "yes" | "no";
}

const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  open,
  title,
  message,
  onClose,
  onDiscard,
  initialFocus = "yes",
}) => {
  const noButtonRef = useRef<HTMLButtonElement>(null);

  const handleClose = (confirmed: boolean) => {
    onClose(confirmed);
  };

  const handleDiscard = () => {
    onDiscard();
  };

  return (
    <ThemeProvider theme={appTheme}>
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === GLOBALMESSAGES.BACKDROP) {
          return;
        }
        if (onDiscard) {
          handleDiscard();
        } else {
          handleClose(false);
        }
      }}
      aria-labelledby="responsive-dialog-title"
      PaperProps={{
    className: "confirmation-dialog-paper"
  }}
      // autoFocus alone loses to openers that refocus themselves after the click - a DataGrid
      // actions cell does (Bug 82805), leaving focus on the dialog root - so re-assert it once
      // the open transition has finished.
      slotProps={{
        transition: {
          onEntered: () => {
            if (initialFocus === "no") {
              noButtonRef.current?.focus();
            }
          },
        },
      }}
    >
      <DialogTitle id="responsive-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => {
          if (onDiscard) {
            handleDiscard();
          } else {
            handleClose(false);
          }
        }}
        className="dialog-close-button"
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Typography gutterBottom>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button
          ref={noButtonRef}
          autoFocus={initialFocus === "no"}
          onClick={() => handleClose(false)}
          color="light"
          variant="contained"
          size="small"
        >
          No
        </Button>
        <Button
          autoFocus={initialFocus === "yes"}
          onClick={() => handleClose(true)}
          variant="contained"
          size="small"
        >
          Yes
        </Button>
      </DialogActions>
    </Dialog>
    </ThemeProvider>
  );
};

export default ConfirmationDialog;
